import { BackgroundExportTypes } from 'further-types/background-exports';
import isMongoId from '../../helpers/is-mongo-id';
import { z } from 'zod';
import {
  InvestmentCustomTagCategory,
  FeeAccounting,
  InvestmentPaymentStatus,
  InvestorTag,
} from 'further-types/investment';
import { FeeExistenceFilterType } from 'further-types/fees';
import { Status } from 'further-types/eis-wizard';

// Base fields for all export types
const baseFields = {
  firmId: z
    .string()
    .optional()
    .refine(isMongoId({ optional: true })),
};

// System export params schema (Cash Transactions, Investment Transactions, Investment Holdings)
const systemExportSchema = z.object({
  exportType: z.enum([
    BackgroundExportTypes.CashTransactionsReport,
    BackgroundExportTypes.InvestorSiteCashTransactionsReport,
    BackgroundExportTypes.InvestmentTransactionsReport,
    BackgroundExportTypes.InvestorSiteInvestmentTransactionsReport,
    BackgroundExportTypes.InvestmentHoldingsReport,
  ]),
  ...baseFields,
  params: z
    .object({
      investorId: z
        .string()
        .optional()
        .refine(isMongoId({ optional: true })),
      adviserId: z
        .string()
        .optional()
        .refine(isMongoId({ optional: true })),
      externalInvestmentId: z.string().optional(),
      dateRange: z
        .object({
          startDate: z.date().optional(),
          endDate: z.date().optional(),
        })
        .optional(),
    })
    .optional(),
});

// Adviser export schema
const adviserExportSchema = z.object({
  exportType: z.enum([
    BackgroundExportTypes.AdviserStatementExport,
    BackgroundExportTypes.AdviserStatementExportAllFirms,
  ]),
  ...baseFields,
  params: z.object({
    adviserId: z
      .string()
      .optional()
      .refine(isMongoId({ optional: true })),
    valuationDate: z.date().optional(),
  }),
});

// Organisation export schema
const organisationExportSchema = z.object({
  exportType: z.enum([BackgroundExportTypes.OrganisationStatementExport]),
  ...baseFields,
  params: z.object({
    organisationId: z
      .string()
      .optional()
      .refine(isMongoId({ optional: true })),
    valuationDate: z.date().optional(),
  }),
});

// Investor export schema
const investorExportSchema = z.object({
  exportType: z.enum([BackgroundExportTypes.InvestorExport]),
  ...baseFields,
  params: z.object({
    fundId: z
      .union([
        z.string().refine(isMongoId({ optional: false })),
        z.literal('no-investment'),
      ])
      .optional(),
    search: z.string().optional(),
    sortType: z.enum(['asc', 'desc']).optional(),
    keyName: z.string().optional(),
  }),
});

// Fee export schema
const feeExportSchema = z.object({
  exportType: z.enum([BackgroundExportTypes.FeesExport]),
  ...baseFields,
  params: z.object({
    funds: z.array(z.string()).optional(),
    fullName: z.string().optional(),
    feeAccounting: z.nativeEnum(FeeAccounting).optional(),
    feeType: z.nativeEnum(FeeExistenceFilterType).optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    exportToCustodian: z.boolean().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    orderBy: z.string().optional(),
  }),
});

// EIS Tasklist export schema
const eisTasklistExportSchema = z.object({
  exportType: z.enum([BackgroundExportTypes.EisTasklistExport]),
  ...baseFields,
  params: z.object({
    status: z.nativeEnum(Status).optional(),
    search: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    orderBy: z.string().optional(),
  }),
});

// Companies export schema
const companiesExportSchema = z.object({
  exportType: z.enum([BackgroundExportTypes.CompaniesExport]),
  ...baseFields,
  params: z.object({
    companyName: z.string().optional(),
    fundId: z.string().optional(),
  }),
});

// Exits export schema
const exitsExportSchema = z.object({
  exportType: z.enum([BackgroundExportTypes.ExitsExport]),
  ...baseFields,
  params: z.object({
    companyName: z.string().optional(),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    orderBy: z.string().optional(),
  }),
});

// Advisers export schema
const advisersExportSchema = z.object({
  exportType: z.enum([BackgroundExportTypes.AdvisersExport]),
  ...baseFields,
  params: z.object({
    organisationId: z
      .string()
      .optional()
      .refine(isMongoId({ optional: true })),
    search: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    orderBy: z.string().optional(),
  }),
});

// Organisations export schema
const organisationsExportSchema = z.object({
  exportType: z.enum([BackgroundExportTypes.OrganisationsExport]),
  ...baseFields,
  params: z.object({
    search: z.string().optional(),
    order: z.enum(['asc', 'desc']).optional(),
    orderBy: z.string().optional(),
  }),
});

// Investments export schema
const investmentsExportSchema = z.object({
  exportType: z.enum([BackgroundExportTypes.InvestmentExport]),
  ...baseFields,
  params: z.object({
    sortType: z.enum(['asc', 'desc']).optional(),
    fundId: z
      .string()
      .optional()
      .refine(isMongoId({ optional: true })),
    fundType: z
      .string()
      .optional()
      .refine(isMongoId({ optional: true })),
    paymentStatus: z.nativeEnum(InvestmentPaymentStatus).optional(),
    searchByInvestorAndInvestment: z.string().optional(),
    minInvestment: z.number().optional(),
    maxInvestment: z.number().optional(),
    customTags: z
      .array(
        z.object({
          category: z.nativeEnum(InvestmentCustomTagCategory),
          tag: z.string(),
        }),
      )
      .optional(),
    investorTags: z.array(z.nativeEnum(InvestorTag)).optional(),
    adviserId: z
      .string()
      .optional()
      .refine(isMongoId({ optional: true })),
    startDate: z.string().optional(),
    endDate: z.string().optional(),
  }),
});

// Billing export schema
const billingExportSchema = z.object({
  exportType: z.enum([BackgroundExportTypes.BillingExport]),
  ...baseFields,
  params: z.object({
    date: z.date(),
    firmIds: z.array(z.string().refine(isMongoId())).min(1),
  }),
});

// Portal usage data report schema
const portalUsageDataReportSchema = z.object({
  exportType: z.enum([BackgroundExportTypes.PortalUsageDataReport]),
  ...baseFields,
  params: z
    .object({
      dateRange: z
        .object({
          startDate: z.date().optional(),
          endDate: z.date().optional(),
        })
        .optional(),
    })
    .optional(),
});

// Create a discriminated union of all export schemas
export const CreateBackgroundExportInput = z.discriminatedUnion('exportType', [
  systemExportSchema,
  adviserExportSchema,
  organisationExportSchema,
  investorExportSchema,
  feeExportSchema,
  eisTasklistExportSchema,
  companiesExportSchema,
  exitsExportSchema,
  advisersExportSchema,
  organisationsExportSchema,
  investmentsExportSchema,
  billingExportSchema,
  portalUsageDataReportSchema,
]);

export const GetBackgroundExportsForFurtherUserInput = z.object({
  firmId: z
    .string()
    .optional()
    .refine(isMongoId({ optional: true })),
});

export const DownloadBackgroundExportInput = z.object({
  exportId: z.string().refine(isMongoId()),
});

export const DeleteBackgroundExportInput = z.object({
  exportId: z.string().refine(isMongoId()),
});
